import type {MouseEvent, PointerEvent, ReactElement} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {Pill} from '@Components/pill/pill';
import {PillSize, PillType} from '@Components/pill/pill.types';
import {BrandKitSwatchCluster} from '@Components/mystuff-brands/components/brand-kit-swatch-cluster/brand-kit-swatch-cluster';
import type {BrandKitRenameControlProps} from './brand-kit-rename-control.types';
import styles from './brand-kit-rename-control.module.scss';

/**
 * The end of a kit's pill or list row, holding one thing at a time: swatches or "Default" tag, on desktop hover the pencil, while renaming the save tick, while saving a spinner.
 * The pill or row reveals the pencil by setting --brand-kit-rename-pencil-opacity to 1 on hover.
 */
export function BrandKitRenameControl({rename, isRenameSaving, cluster, isDefaultTagShown, hasHoverPencil}: BrandKitRenameControlProps): ReactElement {
  /** Pressing the tick must not blur the input first, or the blur would cancel before the click saves. */
  const keepInputFocused = (event: PointerEvent | MouseEvent): void => {
    event.preventDefault();
  };

  const onConfirmClick = (event: MouseEvent): void => {
    event.stopPropagation();
    rename.commitRename();
  };

  const getHoverPencil = (): ReactElement | null => {
    if (!hasHoverPencil || !rename.canRename) {
      return null;
    }

    return (
      <Icon
        className={styles.pencil}
        icon="icon-pencil"
        size={IconSize.SIZE_ICON_16}
        type={IconType.NONE}
        shape={IconShape.NONE}
        isUsedAsButton
        ariaLabel={window.i18next.t('pmwjs_rename')}
        onClick={rename.startRenaming}
      />
    );
  };

  if (isRenameSaving) {
    return (
      <span className="inline-flexbox flex-shrink-0" aria-label={window.i18next.t('pmwjs_saving')} role="status">
        <CircularProgressLoader size={LOADER_SIZE.XSMALL} color={LOADER_COLOR.DARK} />
      </span>
    );
  }

  if (rename.isRenaming) {
    return (
      <span className={`${styles.confirmButton} inline-flexbox flex-items-center flex-content-center flex-shrink-0 corner-radius-full ct-neutral-primary _cursor-pointer`} onPointerDown={keepInputFocused} onMouseDown={keepInputFocused}>
        <Icon
          icon="icon-check"
          size={IconSize.SIZE_ICON_16}
          type={IconType.NONE}
          shape={IconShape.NONE}
          isUsedAsButton
          ariaLabel={window.i18next.t('pmwjs_save')}
          onClick={onConfirmClick}
        />
      </span>
    );
  }

  /** What the pencil fades in over: fades out with the pencil's arrival, and never takes the pencil's clicks. */
  const getClassNameUnderPencil = (): string => {
    return hasHoverPencil && rename.canRename ? `${styles.clusterUnderPencil} _pointer-events-none` : '';
  };

  /** The default kit wears a "Default" tag in its swatches' place, so it reads apart from the rest at a glance. */
  const getRestingContent = (): ReactElement | null => {
    if (isDefaultTagShown) {
      return <Pill label={window.i18next.t('pmwjs_default')} type={PillType.SUCCESS} size={PillSize.SM} icon="icon-check-circle" className={getClassNameUnderPencil()} />;
    }

    return <BrandKitSwatchCluster cluster={cluster} className={getClassNameUnderPencil()} />;
  };

  return (
    <span className={`${styles.renameControlSlot} flex-shrink-0`}>
      {getRestingContent()}
      {getHoverPencil()}
    </span>
  );
}
